import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppHeader from "../../../../../components/AppHeader";
import { getSocket, safeEmit, connectSocket } from "../../../../../api/socket";
import { useSelector } from "react-redux";
import Peer from "simple-peer"; // Added
import "./CallPage.css";

// Metered free Open Relay TURN (static for testing; upgrade for dynamic)
const ICE = {
  iceServers: [
    { urls: "stun:stun.cloudflare.com:3478" },
    { urls: "stun:stun.stunprotocol.org:3478" },
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
    { urls: "turn:openrelay.metered.ca:80", username: "openrelayproject", credential: "openrelayproject" },
    { urls: "turn:openrelay.metered.ca:443", username: "openrelayproject", credential: "openrelayproject" },
    { urls: "turn:openrelay.metered.ca:443?transport=tcp", username: "openrelayproject", credential: "openrelayproject" },
  ],
};

function makeCallId(a, b) {
  const [x, y] = [String(a || ""), String(b || "")].sort();
  const bucket = Math.floor(Date.now() / (1000 * 60 * 5));
  return `${x}__${y}__${bucket}`;
}

export default function CallPage() {
  const { peerId } = useParams();
  const nav = useNavigate();
  const { user } = useSelector((s) => s.auth);
  const userId = user?._id;

  const [err, setErr] = useState("");
  const [inProgress, setInProgress] = useState(false);
  const [remoteReady, setRemoteReady] = useState(false);

  const peerRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(new MediaStream());
  const audioRef = useRef(null);
  const callIdRef = useRef(null);
  const userHungUpRef = useRef(false);
  const isInitiatorRef = useRef(false);

  // timers & state for reliability
  const noAnswerTimerRef = useRef(null);
  const lastOfferRef = useRef(null);
  const offerRetryTimerRef = useRef(null);
  const offerAttemptsRef = useRef(0);

  // Ensure socket connection for this user
  useEffect(() => { if (userId) connectSocket(String(userId)); }, [userId]);

  // Auto-answer flow (when IncomingCallListener stored payload)
  useEffect(() => {
    const raw = localStorage.getItem("incomingCall");
    if (!raw) return;
    try {
      const data = JSON.parse(raw);
      if (data?.to === userId && (data?.from === peerId || !peerId)) {
        console.log("[call] auto-answering stored incoming call", data);
        localStorage.removeItem("incomingCall");
        answerCall(data);
      } else {
        localStorage.removeItem("incomingCall");
      }
    } catch {
      localStorage.removeItem("incomingCall");
    }
  }, [peerId, userId]);

  // Play remote audio when stream available
  useEffect(() => {
    if (audioRef.current && remoteStreamRef.current) {
      audioRef.current.srcObject = remoteStreamRef.current;
      const p = audioRef.current.play();
      if (p?.catch) p.catch(() => {});
    }
  }, [remoteReady]);

  const clearNoAnswer = () => {
    if (noAnswerTimerRef.current) {
      clearTimeout(noAnswerTimerRef.current);
      noAnswerTimerRef.current = null;
    }
  };

  const startNoAnswer = () => {
    clearNoAnswer();
    noAnswerTimerRef.current = setTimeout(() => {
      console.warn("[call] no answer timeout");
      setErr("No answer. Try again later.");
      hangup(false);
    }, 25000);
  };

  const clearOfferRetry = () => {
    if (offerRetryTimerRef.current) {
      clearTimeout(offerRetryTimerRef.current);
      offerRetryTimerRef.current = null;
    }
    offerAttemptsRef.current = 0;
  };

  const scheduleOfferRetry = (otherId) => {
    clearOfferRetry();
    offerAttemptsRef.current += 1;
    if (offerAttemptsRef.current > 8) {
      console.warn("[call] offer retry maxed out");
      setErr("User seems offline. Please try again.");
      return;
    }
    offerRetryTimerRef.current = setTimeout(async () => {
      try {
        if (lastOfferRef.current && isInitiatorRef.current) {
          console.log("[call] re-emitting offer attempt", offerAttemptsRef.current);
          await safeEmit("call:offer", {
            to: otherId,
            from: userId,
            signal: lastOfferRef.current,
            callId: callIdRef.current,
          });
        }
      } catch {}
    }, 1200);
  };

  const getMic = async () => {
    if (!navigator.mediaDevices?.getUserMedia) throw new Error("getUserMedia not available");
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true }); // Voice only
    localStreamRef.current = stream;
    return stream;
  };

  const startCall = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setErr("");
    if (!peerId) { setErr("No recipient specified."); return; }
    if (String(peerId) === String(userId)) { setErr("You can’t call yourself."); return; }

    isInitiatorRef.current = true;
    userHungUpRef.current = false;

    try {
      await connectSocket(String(userId));
      console.log("[call] connecting socket…");
      console.log("[call] socket connected:", !!getSocket()?.connected);

      const stream = await getMic();

      callIdRef.current = makeCallId(userId, peerId);
      console.log("[call] callId =", callIdRef.current);

      const peer = new Peer({
        initiator: true,
        trickle: false,
        config: ICE, // Added for 100% reliability
        stream,
      });

      peer.on("signal", (data) => {
        lastOfferRef.current = data;
        safeEmit("call:offer", {
          to: peerId,
          from: userId,
          signal: data,
          callId: callIdRef.current,
        });
      });

      peer.on("stream", (remoteStream) => {
        remoteStreamRef.current = remoteStream;
        setRemoteReady(true);
      });

      peer.on("connect", () => {
        clearNoAnswer();
      });

      peer.on("error", (error) => {
        console.error("[peer error]", error);
        setErr("Call failed. Try again.");
        cleanup();
      });

      peerRef.current = peer;
      setInProgress(true);
    } catch (e2) {
      console.error(e2);
      const msg = String(e2?.message || e2 || "");
      if (/secure origin|https/i.test(msg)) {
        setErr("Microphone blocked: open the app on HTTPS or localhost.");
      } else if (/Permission|denied|NotAllowed/i.test(msg)) {
        setErr("Microphone permission denied.");
      } else {
        setErr("Microphone access denied or unavailable.");
      }
      cleanup();
    }
  };

  const answerCall = async (incoming) => {
    setErr("");
    isInitiatorRef.current = false;
    userHungUpRef.current = false;

    try {
      await connectSocket(String(userId));
      const stream = await getMic();

      callIdRef.current = incoming.callId || makeCallId(incoming.from, incoming.to);
      console.log("[call] answer callId =", callIdRef.current);

      const peer = new Peer({
        initiator: false,
        trickle: false,
        config: ICE, // Added for 100% reliability
        stream,
      });

      peer.on("signal", (data) => {
        safeEmit("call:answer", {
          to: incoming.from,
          from: userId,
          signal: data,
          callId: callIdRef.current,
        });
      });

      peer.on("stream", (remoteStream) => {
        remoteStreamRef.current = remoteStream;
        setRemoteReady(true);
      });

      peer.on("connect", () => {
        // Call connected
      });

      peer.on("error", (error) => {
        console.error("[peer error]", error);
        setErr("Call failed. Check mic and try again.");
        cleanup();
      });

      peer.signal(incoming.signal);
      peerRef.current = peer;
      setInProgress(true);
    } catch (e2) {
      console.error(e2);
      setErr("Could not answer call. Check mic permission and try again.");
      cleanup();
    }
  };

  // Socket listeners (adapted for Simple-Peer signals)
  useEffect(() => {
    const s = getSocket();
    if (!s) return;

    const onOfferDelivered = (data) => {
      if (!data || data.callId !== callIdRef.current) return;
      if (!isInitiatorRef.current) return;

      if (data.delivered) {
        console.log("[call] offer delivered to callee; starting no-answer timer");
        clearOfferRetry();
        startNoAnswer();
      } else {
        console.log("[call] offer NOT delivered (callee offline). Scheduling retry…");
        scheduleOfferRetry(peerId);
      }
    };

    const onAnswer = (data) => {
      console.log("[socket] call:answer", data);
      if (data.from !== peerId || data.callId !== callIdRef.current) return;
      if (peerRef.current) {
        peerRef.current.signal(data.signal);
        clearNoAnswer();
        clearOfferRetry();
      }
    };

    const onOffer = (data) => {
      console.log("[socket] call:offer", data);
      if (!data || data.from !== peerId) return;
      const my = String(userId || "");
      const their = String(data.from || "");
      if (isInitiatorRef.current && my > their) {
        console.log("[call] glare: switching to callee role");
        cleanup(false);
        answerCall({ ...data, to: my });
      }
    };

    const onEnd = (data) => {
      console.log("[socket] call:end", data);
      if (data.from === peerId && (!data.callId || data.callId === callIdRef.current)) {
        cleanup();
      }
    };

    const onDecline = (data) => {
      console.log("[socket] call:decline", data);
      if (data.from === peerId && (!data.callId || data.callId === callIdRef.current)) {
        setErr("Call declined by recipient.");
        cleanup();
      }
    };

    // Simple-Peer handles ICE candidates internally, so no need for onCand

    s.on("call:offer-delivered", onOfferDelivered);
    s.on("call:answer", onAnswer);
    s.on("call:offer", onOffer);
    s.on("call:end", onEnd);
    s.on("call:decline", onDecline);

    return () => {
      s.off("call:offer-delivered", onOfferDelivered);
      s.off("call:answer", onAnswer);
      s.off("call:offer", onOffer);
      s.off("call:end", onEnd);
      s.off("call:decline", onDecline);
    };
  }, [peerId, userId]);

  const cleanup = (goBack = true) => {
    clearNoAnswer();
    clearOfferRetry();
    if (peerRef.current) peerRef.current.destroy();
    try { localStreamRef.current?.getTracks()?.forEach((t) => t.stop()); } catch {}
    peerRef.current = null;
    localStreamRef.current = null;
    remoteStreamRef.current = new MediaStream();
    setRemoteReady(false);
    setInProgress(false);
    if (goBack && userHungUpRef.current) nav(-1);
  };

  const hangup = (notifyOther = true) => {
    userHungUpRef.current = true;
    if (notifyOther && peerId && userId) {
      safeEmit("call:end", { to: peerId, from: userId, callId: callIdRef.current }).catch(() => {});
    }
    cleanup(true);
  };

  useEffect(() => {
    return () => cleanup(false);
  }, []);

  return (
    <div className="call-page">
      <AppHeader title="Voice Call" onBack={() => hangup(true)} />
      {err && <div className="call-error">{err}</div>}

      {!inProgress && (
        <div className="call-card">
          <button className="btn-brand" type="button" onClick={startCall}>
            Start Call
          </button>
        </div>
      )}

      {inProgress && (
        <div className="call-card">
          <div>Call in progress…</div>
          <button className="btn-light" type="button" onClick={() => hangup(true)}>
            Hang up
          </button>
        </div>
      )}

      {/* Hidden audio element for remote audio */}
      <audio ref={audioRef} autoPlay />
    </div>
  );
}